// Payments/PaymentLinksPage.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Alert, Container, useTheme, alpha, Divider
} from '@mui/material';
import CreatePaymentLinkForm from './CreatePaymentLinkForm';
import PaymentLinksTable from './PaymentLinksTable';
import { hasPermission } from '../../utils/permissionUtils';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';

const PaymentLinksPage = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('payments', 'view');
        const addPerm = await hasPermission('payments', 'add');
        const editPerm = await hasPermission('payments', 'edit');
        const deletePerm = await hasPermission('payments', 'delete');

        setCanView(viewPerm);
        setCanAdd(addPerm);
        setCanEdit(editPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanAdd(false);
        setCanEdit(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '400px' }}>
        <Typography variant="body1" color="textSecondary">Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Access Denied</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You don't have permission to view Payment Link settings. Please contact your administrator.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">
        Payment Links Management
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left Column (Tabs) */}
        <Paper 
          elevation={2}
          sx={{
            minWidth: { md: '220px' },
            height: 'fit-content',
            position: { md: 'sticky' },
            top: { md: 20 },
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            orientation="vertical"
            variant="scrollable"
            TabIndicatorProps={{
              style: {
                backgroundColor: theme.palette.primary.main,
                width: '4px',
                left: 0,
              }
            }}
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                textAlign: 'left',
                px: 3,
                py: 2,
                justifyContent: 'flex-start',
                color: `${theme.palette.text.primary} !important`,
                transition: 'all 0.2s ease',
                minHeight: 48,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.primary,
                },
              },
              '& .MuiTab-root.Mui-selected': {
                color: `${theme.palette.primary.main} !important`,
                backgroundColor: theme.palette.action.selected,
                fontWeight: 'bold',
                '& .MuiSvgIcon-root': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab 
              icon={<LinkIcon />} 
              iconPosition="start" 
              label="Create New Link" 
            />
            <Tab 
              icon={<ListAltIcon />} 
              iconPosition="start" 
              label="Manage Links" 
            />
          </Tabs>
        </Paper>

        {/* Right Column (Content) */}
        <Box sx={{ flex: 1, minWidth: 0 }}> {/* FIX: Added minWidth: 0 to prevent overflow */}
          {tabValue === 0 && (
            <CreateLinkTab 
              onRefresh={handleRefresh} 
              permission={{ canAdd, canEdit, canDelete }} 
            />
          )}
          {tabValue === 1 && (
            <ManageLinksTab 
              refreshKey={refreshKey} 
              onRefresh={handleRefresh} 
              permission={{ canAdd, canEdit, canDelete }} 
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CreateLinkTab = ({ onRefresh, permission }) => (
  <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper' }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }} color="textPrimary">
      Create New Payment Link
    </Typography>
    <CreatePaymentLinkForm onSuccess={onRefresh} permission={permission} />
  </Paper>
);

const ManageLinksTab = ({ refreshKey, onRefresh, permission }) => (
  <Paper elevation={3} sx={{ p: 4, bgcolor: 'background.paper' }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }} color="textPrimary">
      Manage Payment Links
    </Typography>
    <PaymentLinksTable refreshKey={refreshKey} onRefresh={onRefresh} permission={permission} />
  </Paper>
);

export default PaymentLinksPage;